// Purpose: /dropboxes/new/persistent — request persistent dropbox (admin
// approval required). Phase 6 (D3 reversal) adds routing config.
// Phase 7.5 port: PageHeader + Card + Input/Textarea/Select + Button.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestPersistentDropbox } from '../api/dropbox.ts';
import { PageHeader } from '../components/nav/PageHeader.tsx';
import { RoutingPicker, type RoutingPickerValue } from '../components/RoutingPicker.tsx';
import { Button, Card, Input, Select, Textarea } from '../components/ui/index.ts';

export function NewPersistentDropbox() {
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [justification, setJustification] = useState('');
  const [requestedExpiryDays, setRequestedExpiryDays] = useState<30 | 90 | 365>(90);
  const [routing, setRouting] = useState<RoutingPickerValue>({
    config: undefined,
    incomplete: false,
    folderPathWarning: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (label.trim().length === 0 || justification.trim().length < 10) return;
    setSubmitting(true);
    setError(null);
    try {
      await requestPersistentDropbox({
        label: label.trim(),
        justification: justification.trim(),
        requestedExpiryDays,
        ...(routing.config ? { routing: routing.config } : {}),
      });
      setSubmitted(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col gap-6 max-w-2xl">
        <PageHeader
          title="Request submitted"
          subtitle='An administrator will review your request. You will see the dropbox in your list with status "Pending approval" until then. Once approved, the link is sent to you and you can share it.'
        />
        <div>
          <Button variant="secondary" onClick={() => navigate('/dropboxes')}>
            Back to dropboxes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <PageHeader
        title="Request persistent dropbox"
        subtitle="Persistent dropboxes stay live for 30, 90, or 365 days and require admin approval. Examples: HR onboarding intake, vendor compliance document submission. Use ephemeral if you only need a one-off."
      />
      <Card padding="comfortable">
        <div className="flex flex-col gap-5">
          <Input
            label="Label (visible to uploader)"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Apogee HR Onboarding Documents"
            maxLength={200}
          />
          <Textarea
            label="Justification"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Why does this need to be persistent? Who will be uploading? What is the data sensitivity?"
            maxLength={2000}
            rows={6}
            helpText={`${justification.length} / 2000 characters. Minimum 10.`}
          />
          <Select
            label="Requested expiry"
            value={requestedExpiryDays}
            onChange={(e) => setRequestedExpiryDays(Number(e.target.value) as 30 | 90 | 365)}
            helpText="The approver may set a shorter expiry than you requested."
          >
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </Select>

          <RoutingPicker value={routing} onChange={setRouting} onError={setError} />

          {error ? (
            <div
              role="alert"
              className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
            >
              {error}
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => navigate('/dropboxes')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={submitting}
              disabled={
                label.trim().length === 0 ||
                justification.trim().length < 10 ||
                routing.incomplete ||
                !!routing.folderPathWarning
              }
              onClick={() => void handleSubmit()}
            >
              Submit for approval
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
